import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FileUploadModule, FileUploadValidators } from '@iplab/ngx-file-upload';
import { AdministrationService } from '../../../services/administration.service';
import { FirmaElectronica } from '../../../models/User';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScnackbarData, ToastService, SnackbarStyles } from '../../../commons/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { AppTitleService } from '../../../services/app-title.service';
import { DlgConfirmComponent, DlgConfirmComponentData } from '../../../commons/dlg-confirm/dlg-confirm.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';

export function fileRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const files: File[] = control.value;
    return files && files.length > 0 ? null : { required: true };
  };
}

@Component({
    selector: 'app-firma-electronica',
    imports: [
        ReactiveFormsModule, MatFormFieldModule,
        MatToolbarModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        FileUploadModule, MatTooltipModule,
        MatProgressSpinnerModule,
        MatCardModule,
        MatListModule
    ],
    templateUrl: './firma-electronica.component.html',
    styleUrl: './firma-electronica.component.scss'
})
export class FirmaElectronicaComponent implements OnInit {
  filesLimitMessageError = ""
  numberOfFilesLimit = 1
  filesSizeLimit = 41943040
  usedKB = 0

  fetchingUpdateSignature = false

  private filesControl = new FormControl<File[]>([], [
    fileRequiredValidator(),
    FileUploadValidators.filesLimit(this.numberOfFilesLimit),
    FileUploadValidators.sizeLimit(this.filesSizeLimit)
  ]);

  addSignatureForm = new FormGroup({
    signatureStatus: new FormControl(""),
    signature: this.filesControl,
    desc: new FormControl("", [

    ])
  })

  userSignature: FirmaElectronica | undefined

  snackbarData: ScnackbarData = {}

  constructor(
    private administrationService: AdministrationService,
    private router: Router,
    public dlg: MatDialog,
    private toast: ToastService
  ) {
    inject(AppTitleService).setTitle("Administraci\xf3n");
    this.filesLimitMessageError = `M\xe1ximo ${this.numberOfFilesLimit} ${this.numberOfFilesLimit > 1 ? 'archivos' : 'archivo'} permitido${this.numberOfFilesLimit > 1 ? 's' : ''}`
  }

  ngOnInit(): void {
    this.addSignatureForm.get('signature')?.valueChanges.subscribe(files => {
      if (files) {
        this.updateUsedKB(files);
      }
    });
    this.administrationService.GetCurrentUserSignatureData().subscribe({
      next: (response) => {
        this.userSignature = response
        this.setSignatureStatusFormControlValue()
      }
    })
  }

  fetchPatchCurrentUserSignature() {
    this.fetchingUpdateSignature = true
    const signature = this.addSignatureForm.controls.signature.value!.at(0)
    const desc = this.addSignatureForm.controls.desc.value!
    this.administrationService.PatchCurrentUserSignatureData(signature!, desc).subscribe({
      next: (response) => {
        this.userSignature = response
        this.snackbarData.message = "Firma actualizada"
        this.snackbarData.style = SnackbarStyles.Success
        this.toast.show(this.snackbarData)
        this.setSignatureStatusFormControlValue()
        this.fetchingUpdateSignature = false
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 406) {
          this.snackbarData.message = "No actualizado! revisar sus datos."
          this.snackbarData.style = SnackbarStyles.Error
          this.toast.show(this.snackbarData)
        } else {
          this.snackbarData.message = "Error actualizando"
          this.snackbarData.style = SnackbarStyles.Error
          this.toast.show(this.snackbarData)
        }
        this.fetchingUpdateSignature = false
      }
    })
  }


  setSignatureStatusFormControlValue() {
    if (this.userSignature!.firma) {
      this.addSignatureForm.controls.signatureStatus.setValue("REGISTRADA")
    } else {
      this.addSignatureForm.controls.signatureStatus.setValue("NO REGISTRADA")
    }
  }

  updateUsedKB(files: File[]) {
    this.usedKB = files.reduce((total, file) => total + file.size / 1024, 0); // Convertir bytes a KB
    this.updateKBsUsedDisplay();
  }

  updateKBsUsedDisplay() {
    const KB = this.filesSizeLimit / 1024
    return `${this.usedKB.toFixed(2)}/${KB.toFixed(0)} KB`;
  }

  onBack() {
    this.router.navigate(["/administracion"]);
  }

  onQuitarFirma() {
    const data: DlgConfirmComponentData = {
      title: "Quitar firma",
      message: "¿Seguro desea quitar su firma electr\u00f3nica?",
      confirmLabel: "Si, quitar",
      closeLabel: "No"
    }
    const dialogRef = this.dlg.open(DlgConfirmComponent, {
      maxWidth: "40vw",
      width: "400px",
      data: data
    })
    this.fetchingUpdateSignature = true
    dialogRef.afterClosed().subscribe((result: string | undefined) => {
      if (result) {
        this.administrationService.removeSignature().subscribe({
          next: (res: any) => {
            this.userSignature = res
            this.snackbarData.message = "Firma removida"
            this.snackbarData.style = SnackbarStyles.Success
            this.toast.show(this.snackbarData)
            this.setSignatureStatusFormControlValue()
            this.fetchingUpdateSignature = false
          },
          error: (error: HttpErrorResponse) => {
            if (error.status === 406) {
              this.snackbarData.message = "SIn cambios! revisar sus datos."
              this.snackbarData.style = SnackbarStyles.Error
              this.toast.show(this.snackbarData)
            } else {
              this.snackbarData.message = "Error removiendo"
              this.snackbarData.style = SnackbarStyles.Error
              this.toast.show(this.snackbarData)
            }
            this.fetchingUpdateSignature = false
          }
        })
      }
    });
  }
}
